import React from "react";
import styled from "styled-components";

import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import { Direction } from "@enums/direction";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";

import type { SxProps, Theme } from "@mui/material";

interface TableHeadProps<T, D, X> {
  sx?: SxProps<Theme>;
  numSelected?: number;
  visibleRows?: T[];
  onRequestSort?: (event: React.MouseEvent<unknown>, property: keyof T) => void;
  sortDirection?: X;
  rowCount?: number;
  withSort?: boolean;
  withCheckBox?: boolean;
  orderBy?: keyof T;
  headCells: D[];
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function TableHeader<T, D extends Record<string, any>, X extends Direction>({
  sx,
  orderBy,
  rowCount,
  visibleRows,
  numSelected,
  onRequestSort,
  sortDirection,
  headCells = [],
  onSelectAllClick,
  withCheckBox = true,
  withSort = true,
}: TableHeadProps<T, D, X>) {
  const createSortHandler =
    (property: keyof T) => (event: React.MouseEvent<unknown>) => {
      if (withSort && onRequestSort) onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {withCheckBox && (
          <TableCell padding="checkbox">
            <Checkbox
              disabled={!visibleRows?.length}
              color="primary"
              indeterminate={numSelected! > 0 && numSelected! < rowCount!}
              checked={rowCount! > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all desserts",
              }}
            />
          </TableCell>
        )}

        {headCells.map((headCell) => (
          <TableCell
            sx={sx}
            key={headCell.id}
            width={headCell.width}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? sortDirection : false}
          >
            {!headCell.sort && <CellTitle>{headCell.label}</CellTitle>}

            {headCell.sort && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={sortDirection}
                onClick={createSortHandler(headCell.id as keyof T)}
              >
                <CellTitle>{headCell.label}</CellTitle>

                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortDirection === Direction.Desc
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const CellTitle = styled.span`
  text-wrap: nowrap !important;
  white-space: nowrap !important;
`;

export default TableHeader;
